import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminGate } from "@/components/auth/AdminGate";
import { AppErrorBoundary } from "@/components/utility/AppErrorBoundary";
import { PwaInstallPrompt } from "@/components/pwa/PwaInstallPrompt";
import NotFound from "./pages/NotFound";

// Lazy load all routes to reduce initial bundle size and allow app to mount first
const PublicHomepage = lazy(() => import("./pages/PublicHomepage"));
const Auth = lazy(() => import("./pages/Auth"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminSources = lazy(() => import("./pages/AdminSources"));
const AdminSearchMethods = lazy(() => import("./pages/AdminSearchMethods"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminIngestionHealth = lazy(() => import("./pages/AdminIngestionHealth"));
const AdminDuplicates = lazy(() => import("./pages/AdminDuplicates"));
const AdminSocial = lazy(() => import("./pages/AdminSocial"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));

const queryClient = new QueryClient();


const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <TooltipProvider>
          <AuthProvider>
            <Toaster />
            <SonnerToaster />
            <PwaInstallPrompt />
            <BrowserRouter>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div><p>Loading...</p></div></div>}>
                <AppErrorBoundary>
                  <Routes>
                    {/* Public routes - no auth required */}
                    <Route path="/" element={<PublicHomepage />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    
                    {/* Auth route */}
                    <Route path="/auth" element={<Auth />} />
                    
                    {/* Protected admin routes */}
                    <Route path="/events" element={<AdminGate><AdminEvents /></AdminGate>} />
                    <Route path="/analytics" element={<AdminGate><Analytics /></AdminGate>} />
                    <Route path="/admin/sources" element={<AdminGate><AdminSources /></AdminGate>} />
                    <Route path="/admin/search-methods" element={<AdminGate><AdminSearchMethods /></AdminGate>} />
                    <Route path="/admin/users" element={<AdminGate><AdminUsers /></AdminGate>} />
                    <Route path="/admin/ingestion-health" element={<AdminGate><AdminIngestionHealth /></AdminGate>} />
                    <Route path="/admin/duplicates" element={<AdminGate><AdminDuplicates /></AdminGate>} />
                    <Route path="/admin/social" element={<AdminGate><AdminSocial /></AdminGate>} />
                    <Route path="/admin" element={<Navigate to="/events" replace />} />
                    
                    {/* Catch-all for 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AppErrorBoundary>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
